import React, {useState, useMemo} from "react"
import {
	Box,
	Modal,
	Paper,
	Typography,
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Slider,
	IconButton,
	Divider,
} from "@material-ui/core"
import {ArrowBack, ArrowForward, Warning} from "@material-ui/icons"
import styled from "styled-components"

import {api} from "@indebted/api"
import {useAgent} from "@indebted/hooks/Agent"

type ExtendInstalmentCapModalProps = {
	onClose: () => void
	customerID: string
	currentMaxPayments: number
	currentMinPayment: number
	totalCustomerBalance: number
	initialValue: number
	onSuccess: () => void
}

const ExtendInstalmentCapModal = ({
	onClose,
	customerID,
	currentMaxPayments,
	currentMinPayment,
	totalCustomerBalance,
	initialValue,
	onSuccess,
}: ExtendInstalmentCapModalProps) => {
	const [selectedMaxPayments, setSelectedMaxPayments] = useState(initialValue)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [submitError, setSubmitError] = useState<string | null>(null)
	const [agentEmail] = useAgent()

	const paymentFrequencies = useMemo(() => {
		const baseAmount = totalCustomerBalance
		const today = new Date()

		const calculateEndDate = (frequency: string, payments: number): string => {
			const endDate = new Date(today)

			switch (frequency) {
				case "Weekly":
					endDate.setDate(endDate.getDate() + payments * 7)
					break
				case "Fortnightly":
					endDate.setDate(endDate.getDate() + payments * 14)
					break
				case "Monthly":
					endDate.setMonth(endDate.getMonth() + payments)
					break
			}

			const day = String(endDate.getDate()).padStart(2, "0")
			const month = String(endDate.getMonth() + 1).padStart(2, "0")
			const year = endDate.getFullYear()

			return `${day}/${month}/${year}`
		}

		const fortnightlyPayments = Math.ceil(selectedMaxPayments / 2)
		const monthlyPayments = Math.ceil(selectedMaxPayments / 4.33)

		return [
			{
				frequency: "Weekly",
				maxPayments: selectedMaxPayments,
				minPayment: `$${(baseAmount / selectedMaxPayments).toFixed(2)}`,
				endDate: calculateEndDate("Weekly", selectedMaxPayments),
			},
			{
				frequency: "Fortnightly",
				maxPayments: fortnightlyPayments,
				minPayment: `$${(baseAmount / fortnightlyPayments).toFixed(2)}`,
				endDate: calculateEndDate("Fortnightly", fortnightlyPayments),
			},
			{
				frequency: "Monthly",
				maxPayments: monthlyPayments,
				minPayment: `$${(baseAmount / monthlyPayments).toFixed(2)}`,
				endDate: calculateEndDate("Monthly", monthlyPayments),
			},
		]
	}, [selectedMaxPayments, totalCustomerBalance])

	const handleSliderChange = (event: any, newValue: number | number[]) => {
		setSelectedMaxPayments(newValue as number)
	}

	const handleDecrease = () => {
		if (selectedMaxPayments > currentMinPayment) {
			setSelectedMaxPayments(selectedMaxPayments - 1)
		}
	}

	const handleIncrease = () => {
		if (selectedMaxPayments < currentMaxPayments) {
			setSelectedMaxPayments(selectedMaxPayments + 1)
		}
	}

	const handleConfirm = async () => {
		try {
			setIsSubmitting(true)
			setSubmitError(null)
			await api.customer.extendInstalmentCap(customerID, selectedMaxPayments, agentEmail)
			onSuccess()
		} catch (err: any) {
			const errorMessage = err?.Message || "Failed to extend instalment cap"
			setSubmitError(errorMessage)
			console.error("Error extending instalment cap:", err)
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<Modal
			open
			onClose={onClose}
			aria-labelledby="extend-instalment-cap-modal"
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<StyledPaper>
				<ModalHeader>
					<Box flex={1} textAlign="center">
						<Typography variant="h5" gutterBottom>
							Extend instalment cap
						</Typography>
						<Typography variant="body1" color="textSecondary">
							Use the slider to increase the maximum number of payments
						</Typography>
					</Box>
				</ModalHeader>

				<StyledDivider />

				{/* Slider Section */}
				<SliderSection>
					<SliderControls>
						<StyledIconButton onClick={handleDecrease} disabled={selectedMaxPayments <= currentMinPayment}>
							<ArrowBack style={{color: "#414651"}} />
						</StyledIconButton>

						<Box flex={1}>
							<SliderValueSection>
								<Typography variant="caption" align="center" display="block">
									Current maximum
								</Typography>
								<SliderValueDisplay align="center">{selectedMaxPayments}</SliderValueDisplay>
								<Typography variant="caption" align="center" display="block">
									Weekly
								</Typography>
							</SliderValueSection>
						</Box>

						<StyledIconButton onClick={handleIncrease} disabled={selectedMaxPayments >= currentMaxPayments}>
							<ArrowForward style={{color: "#414651"}} />
						</StyledIconButton>
					</SliderControls>

					<SliderRangeContainer>
						<SliderContainer>
							<Typography variant="body2" color="textSecondary">
								{currentMinPayment}
							</Typography>
							<Typography variant="body2" color="textSecondary" style={{marginLeft: "auto"}}>
								{currentMaxPayments}
							</Typography>
						</SliderContainer>

						<StyledSlider
							value={selectedMaxPayments}
							onChange={handleSliderChange}
							min={currentMinPayment}
							max={currentMaxPayments}
							step={1}
						/>
					</SliderRangeContainer>
				</SliderSection>

				{selectedMaxPayments === currentMaxPayments ? (
					<MaximumReachedAlert>
						<AlertIconWrapper>
							<Warning style={{color: "#d92d20", fontSize: "20px"}} />
						</AlertIconWrapper>
						<AlertContent>
							<Typography variant="body2" style={{fontWeight: "bold"}}>
								Notice: Maximum instalment cap extension reached
							</Typography>
							<Typography variant="body2">
								The instalment cap selected is the maximum extension permitted by the platform.
								Instalments cannot be increased beyond this point.
							</Typography>
						</AlertContent>
					</MaximumReachedAlert>
				) : (
					<StyledAlert>
						<Typography variant="body2" style={{fontWeight: "bold"}}>
							Notice: Instalments beyond this point will exceed the current maximum
						</Typography>
						<Typography variant="body2">
							Any additional instalments will exceed the default cap currently in place for this customer.
						</Typography>
					</StyledAlert>
				)}

				{/* Error Alert */}
				{submitError && (
					<ErrorAlert>
						<Typography variant="body2" style={{fontWeight: "bold"}}>
							Error
						</Typography>
						<Typography variant="body2">{submitError}</Typography>
					</ErrorAlert>
				)}

				{/* Impact Table */}
				<TableSection>
					<TableContainer component={StyledCard}>
						<CardHeader>
							<Typography variant="body1">Impact by payment frequency</Typography>
						</CardHeader>
						<Table size="small">
							<StyledTableHead>
								<TableRow>
									<StyledHeaderCell>Frequency</StyledHeaderCell>
									<StyledHeaderCell align="center">Max # of payments</StyledHeaderCell>
									<StyledHeaderCell>Min payment</StyledHeaderCell>
									<StyledHeaderCell align="right">End date(if started today)</StyledHeaderCell>
								</TableRow>
							</StyledTableHead>
							<TableBody>
								{paymentFrequencies.map((row) => (
									<StyledTableRow key={row.frequency} highlighted={row.frequency === "Weekly"}>
										<StyledTableCell>
											<Typography variant="body2">{row.frequency}</Typography>
										</StyledTableCell>
										<StyledTableCell align="center">
											<Typography variant="body2">{row.maxPayments}</Typography>
										</StyledTableCell>
										<StyledTableCell>
											<Typography variant="body2">{row.minPayment}</Typography>
										</StyledTableCell>
										<StyledTableCell align="right">
											<Typography variant="body2">{row.endDate}</Typography>
										</StyledTableCell>
									</StyledTableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</TableSection>

				{/* Action Buttons */}
				<ButtonSection>
					<StyledCancelButton variant="outlined" fullWidth onClick={onClose} disabled={isSubmitting}>
						CANCEL
					</StyledCancelButton>
					<StyledConfirmButton
						variant="contained"
						color="primary"
						fullWidth
						onClick={handleConfirm}
						disabled={isSubmitting || selectedMaxPayments === initialValue}
					>
						{isSubmitting ? "CONFIRMING..." : "CONFIRM"}
					</StyledConfirmButton>
				</ButtonSection>
			</StyledPaper>
		</Modal>
	)
}

// Styled Components
const StyledPaper = styled(Paper)`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 90%;
	max-width: 700px;
	max-height: 90vh;
	overflow-y: auto;
	padding: 24px;
	outline: none;
	border-radius: 4px;
`

const ModalHeader = styled(Box)`
	display: flex;
	align-items: flex-start;
	margin-bottom: 1rem;
	position: relative;
`

const StyledDivider = styled(Divider)`
	margin-bottom: 1.5rem;
`

const SliderSection = styled(Box)`
	margin-bottom: 24px;
`

const SliderControls = styled(Box)`
	display: flex;
	align-items: center;
	gap: 21px;
	margin-bottom: 16px;
`

const StyledIconButton = styled(IconButton)`
	background-color: #f6f6f6;
	border-radius: 8px;
	width: 40px;
	height: 40px;

	&:hover {
		background-color: #e9eaeb;
	}

	&:disabled {
		background-color: #f6f6f6;
		opacity: 0.5;
	}
`

const SliderValueSection = styled(Box)`
	text-align: center;
`

const SliderValueDisplay = styled(Typography)`
	font-size: 30px;
	font-weight: bold;
	margin: 8px 0;
	color: #000244;
`

const SliderRangeContainer = styled(Box)`
	margin-top: 16px;
`

const SliderContainer = styled(Box)`
	display: flex;
	justify-content: space-between;
	margin-bottom: 8px;
`

const StyledSlider = styled(Slider)`
	color: #474cde;

	.MuiSlider-thumb {
		background-color: white;
		border: 2px solid #474cde;
		width: 24px;
		height: 24px;
		margin-left: -12px;

		&:hover,
		&.Mui-focusVisible {
			box-shadow: 0px 0px 0px 8px rgba(71, 76, 222, 0.16);
		}
	}

	.MuiSlider-track {
		background-color: #d92d20;
		height: 8px;
		margin-top: 3px;
		border-radius: 4px;
	}

	.MuiSlider-rail {
		background-color: #f6f6f6;
		height: 8px;
		border-radius: 4px;
		border: 1px solid #e9eaeb;
		margin-top: 2px;
	}
`

const StyledAlert = styled(Box)`
	margin-bottom: 24px;
	background-color: #fef0c7;
	border-left: 4px solid #f79009;
	padding: 16px;
	border-radius: 8px;
	display: flex;
	flex-direction: column;
	gap: 4px;
	color: #7a271a;
	font-size: 14px;
	line-height: 1.4;
`

const MaximumReachedAlert = styled(Box)`
	margin-bottom: 24px;
	background-color: #fde7e7;
	border-left: 4px solid #d92d20;
	padding: 16px;
	border-radius: 8px;
	display: flex;
	flex-direction: row;
	gap: 12px;
	color: #7a271a;
	font-size: 14px;
	line-height: 1.4;
	align-items: flex-start;
`

const AlertIconWrapper = styled(Box)`
	display: flex;
	align-items: center;
	flex-shrink: 0;
`

const AlertContent = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: 4px;
`

const ErrorAlert = styled(Box)`
	margin-bottom: 24px;
	background-color: #fee;
	border-left: 4px solid #d32f2f;
	padding: 16px;
	border-radius: 8px;
	display: flex;
	flex-direction: column;
	gap: 4px;
	color: #7a271a;
	font-size: 14px;
	line-height: 1.4;
`

const TableSection = styled(Box)`
	margin-bottom: 24px;
`

const StyledCard = styled(Box)`
	background-color: #f9f9fe;
	border-radius: 8px;
	overflow: hidden;
`

const CardHeader = styled(Box)`
	padding: 16px;
	background-color: #f9f9fe;
`

const StyledTableHead = styled(TableHead)`
	background-color: #f9f9fe;
`

const StyledHeaderCell = styled(TableCell)`
	color: #000244;
	font-size: 0.875rem;
	font-style: normal;
	font-weight: 500;
	line-height: 1.5rem;
	letter-spacing: 0.01063rem;
	padding: 16px;
`

const StyledTableRow = styled(TableRow)<{highlighted?: boolean}>`
	background-color: ${(props) => (props.highlighted ? "#ecedfc" : "#f9f9fe")};
	border-bottom: ${(props) => (props.highlighted ? "1px solid #474cde" : "1px solid #e9eaeb")};
`

const StyledTableCell = styled(TableCell)`
	color: #304777;

	font-size: 0.875rem;
	font-style: normal;
	font-weight: 400;
	line-height: 143%; /* 1.25125rem */
	letter-spacing: 0.01063rem;
	padding: 1rem;
	border-bottom: inherit;
	background: inherit;
`

const ButtonSection = styled(Box)`
	display: flex;
	gap: 8px;
`

const StyledCancelButton = styled(Button)`
	text-transform: uppercase;
	font-weight: 500;
	font-size: 15px;
	letter-spacing: 0.46px;
	border-color: #474cde;
	color: #474cde;
	padding: 8px 22px;

	&:hover {
		border-color: #474cde;
		background-color: rgba(71, 76, 222, 0.04);
	}
`

const StyledConfirmButton = styled(Button)`
	text-transform: uppercase;
	font-weight: 500;
	font-size: 15px;
	letter-spacing: 0.46px;
	background-color: #474cde;
	color: white;
	padding: 8px 22px;
	box-shadow:
		0px 1px 5px 0px rgba(0, 0, 0, 0.12),
		0px 2px 2px 0px rgba(0, 0, 0, 0.14),
		0px 3px 1px -2px rgba(0, 0, 0, 0.2);

	&:hover {
		background-color: #3a3fb8;
	}
`

export {ExtendInstalmentCapModal}
export type {ExtendInstalmentCapModalProps}
